import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { AppView } from 'design-system/AppView';
import Spacer from 'design-system/Spacer';
import { rem } from 'design-system/values';
import StatusBar from 'design-system/StatusBar';
import { styles as textStyles } from 'design-system/Text';
import useColors from 'hooks/useColors';
import useStyles from 'hooks/useStyles';
import createStyleSheets from 'utils/createStyleSheets';
import { useAutoFocusInput } from 'hooks/useAutoFocusInput';
import TextInput from 'utils/packages/TextInput';
import { useAppSelector } from 'store/hooks';
import { selectAccountById } from 'reducers/selectors';
import emoji from 'node-emoji';
import Alert from 'utils/packages/Alert';
import { KeyboardAvoidingContainerNew } from 'design-system/KeyboardAvoidingContainerNew';
import { KeyboardAvoidingButton } from 'design-system/KeyboardAvoidingButton';
import { isWeb } from '../../../constants';
const strings = {
    screenTitle: 'Name the account',
    rename: 'Rename',
    placeholder: 'Choose Name',
    next: 'Continue',
    errorTitle: 'Hold on :dizzy:',
    errorMessage: 'You must enter a name.',
};
const DummyAccountSetupScreen = ({ navigation, route }) => {
    const colors = useColors();
    const styles = useStyles(styleSet);
    const name = useAppSelector((store) => selectAccountById(store, route.params.id)?.name);
    useLayoutEffect(() => {
        navigation.setOptions({
            headerTintColor: colors.text.primary,
            title: route.params.isChangingName ? strings.rename : strings.screenTitle,
        });
    }, [colors.text.primary, route.params.isChangingName]);
    const [value, setValue] = useState(name || '');
    useEffect(() => {
        if (name && !value) {
            setValue(name);
        }
    }, [name]);
    const textInputRef = useRef(null);
    useAutoFocusInput(textInputRef);
    const onSubmit = useCallback(async () => {
        if (value.length < 1) {
            Alert.alert(emoji.emojify(strings.errorTitle), strings.errorMessage);
            return;
        }
        const keyBack = route.params.keyBack || (isWeb ? undefined : route.key);
        const { isChangingName } = route.params;
        const params = {
            ...route.params,
            keyBack,
            name: value,
        };
        if (isChangingName) {
            navigation.navigateDeprecated('DummyAccountCreated', params);
        }
        else {
            navigation.navigateDeprecated('DummyAccountLogoType', params);
        }
    }, [route.key, route.params, value]);
    const onChangeText = useCallback((text) => {
        setValue(text);
    }, []);
    useAutoFocusInput(textInputRef);
    const textInput = useMemo(() => (<TextInput ref={textInputRef} style={styles.textInput} value={value} placeholder={strings.placeholder} placeholderTextColor={colors.text.placeholder} selectionColor={colors.text.selection} underlineColorAndroid="transparent" textAlign="center" autoCapitalize="words" onChangeText={onChangeText} onSubmitEditing={onSubmit} focusStyle={styles.textInputFocus}/>), [colors, styles, value, onChangeText, onSubmit]);
    return (<AppView type={isWeb ? 'secondary' : 'primary'} isInTab={isWeb} avoidHeader>
      <StatusBar barStyle="dark-content" animated/>
      <KeyboardAvoidingContainerNew flexContainer withButtonSpace>
        {isWeb ? <Spacer h={72}/> : <Spacer flex/>}
        {textInput}
        <Spacer flex/>
      </KeyboardAvoidingContainerNew>
      <KeyboardAvoidingButton brand title={strings.next} onPress={onSubmit} disabled={!value.length} withFrame={false}/>
    </AppView>);
};
const styleSet = createStyleSheets((colors) => ({
    textInputFocus: {
        color: colors.text.primary,
    },
    textInput: {
        ...(isWeb
            ? {
                paddingVertical: rem(16),
                color: colors.text.secondary,
            }
            : {
                ...textStyles.TextThin_20,
                alignSelf: 'stretch',
                color: colors.text.primary,
                fontSize: rem(26),
                lineHeight: undefined,
            }),
    },
}));
export default DummyAccountSetupScreen;
